import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  Avatar,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Backdrop,
  CircularProgress,
  Skeleton,
  Grid,
  useMediaQuery,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  ExpandMore,
  WorkOutline,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import { encryptId } from "../../Components/CryptoComponent";
import SelectInput from "../../Components/InputComponents/SelectInput";
import { errorNotify } from "../../Components/ToastComponent/ToastNotification";
import TextInput from "../../Components/InputComponents/TextInput";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const ClientCandidateList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const candidateId =
    sessionStorage?.getItem(`SWA_UserId`) ||
    localStorage?.getItem(`SWA_UserId`);

  const [CandidateData, setCandidateData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loaderOpen, setLoaderOpen] = useState(true);

  const [expandedIds, setExpandedIds] = useState("");

  const handleAccordionChange = (id) => {
    if (expandedIds == id) {
      setExpandedIds("");
    } else {
      setExpandedIds(id);
    }
    navigate("/Candidates", { replace: true }); // removes the old route state
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleApplyFilters = () => {
    getData();
    setPage(0);
  };

  const [filterData, setFilterData] = useState({
    name: "",
    profession: "",
    jobapplication: "",
    jobid: "",
    interviewdate: "",
  });

  const paginationState = {
    pagination: { page, rowsPerPage },
    filterData: filterData,
    from: `Candidates`,
  };

  const handleResetFilters = () => {
    const resetFilters = {
      name: "",
      profession: "",
      jobapplication: "",
      jobid: "",
      interviewdate: "",
    };

    const isAlreadyReset = Object.entries(resetFilters).every(
      ([key, value]) => filterData[key] === value
    );

    if (!isAlreadyReset) {
      setFilterData(resetFilters);
      getData(resetFilters);
    }

    navigate(`/Candidates`, { replace: true });
  };

  const navigatetoView = (id) => {
    navigate(`/Candidates/View`, {
      state: {
        ...paginationState,
        selectedId: JSON.stringify(id),
      },
    });
  };

  const navigatetoJobView = (id) => {
    navigate(`/Jobs/View/${id?.JA_Id}`, {
      state: {
        ...paginationState,
        fromRoute: "candidates",
      },
    });
  };

  const [professionList, setProfessionList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [jobList, setJobList] = useState([]);

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getStatus = async () => {
    try {
      const res = await axiosAPI.get(`/JobApplicationStatus/list`);
      setStatusList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const clientId = sessionStorage?.getItem(`SWA_UserId`) || localStorage?.getItem(`SWA_UserId`);

  const getJobs = async () => {
    const trimmedFilters = {
      JD_Title: ``,
      JD_Country: ``,
      JD_Qualification: ``,
      JD_Profession: ``,
      JD_Status: ``,
    };

    try {
      const res = await axiosAPI?.post(`/clientportal/joblist/${clientId}`,trimmedFilters);
      setJobList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilterData({
      ...filterData,
      [name]: value,
    });
  };

  const getData = (filter = filterData) => {
    const trimmedFilters = {
      CI_Name: filter?.name, // name
      CI_Profession: filter?.profession, // profession //
      JASL_Id: filter?.jobapplication, // jpb application status //
      Jobid: filter?.jobid, // job list
      JA_InterviewDate: filter?.interviewdate, // date
    };

    axiosAPI
      ?.post(`clientportal/Candidatelist/${candidateId}`, trimmedFilters)

      .then((res) => {
        setCandidateData(res?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getProfession();
    getJobs();
    getStatus();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (location?.state?.pagination) {
      setPage(location.state.pagination.page || 0);
      setRowsPerPage(location.state.pagination.rowsPerPage || 25);
    }

    if (location?.state?.filterData) {
      setFilterData(location.state.filterData);
      getData(location.state.filterData); // fetch with restored filters
    } else {
      getData();
    }
  }, [location?.state, page, rowsPerPage]);

  const isMobile = useMediaQuery("(max-width: 1220px)");

  return (
    <Paper
      sx={{
        maxWidth: isMobile ? "100vw" : `calc(100vw - 300px)`,
        padding: 3,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Candidates
        </Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ width: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Name
            </Typography>
            <TextField
              placeholder="Name"
              variant="outlined"
              size="small"
              value={filterData.name}
              onChange={(e) =>
                setFilterData((prev) => ({ ...prev, name: e.target.value }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/* Profession */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Profession
            </Typography>
            <SelectInput
              value={filterData?.profession}
              name="profession"
              onChange={handleFilterChange}
              options={professionList?.map((item) => ({
                value: item?.PI_Id,
                label: item?.PI_Name,
              }))}
              placeholder="Select Profession"
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>

          {/*  Job Application */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job Application Status
            </Typography>
            <SelectInput
              value={filterData.jobapplication}
              name="jobapplication"
              onChange={handleFilterChange}
              options={statusList?.map((item) => ({
                value: item?.JASL_Id,
                label: item?.JASL_Name,
              }))}
              placeholder="Select Status"
            />
          </Grid>

          {/* Job */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Job
            </Typography>
            <SelectInput
              value={filterData.jobid}
              name="jobid"
              onChange={handleFilterChange}
              options={jobList?.map((item) => ({
                value: item?.JD_Id,
                label: item?.JD_Title,
              }))}
              placeholder="Select Job"
            />
          </Grid>

          {/*  Interview Date */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Interview Date
            </Typography>
            <TextInput
              placeholder="Name"
              variant="outlined"
              size="small"
              type="date"
              value={filterData.interviewdate}
              onChange={(e) =>
                setFilterData((prev) => ({
                  ...prev,
                  interviewdate: e.target.value,
                }))
              }
              sx={{ width: { xs: "100%" } }}
            />
          </Grid>
        </Grid>

        {/* job list */}
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<Search />}
          onClick={handleApplyFilters}
          sx={{
            textTransform: "capitalize",
            color: "white",
            background: "#006CB8",
          }}
        >
          Search
        </Button>
        <Button
          variant="outlined"
          startIcon={<RestartAlt />}
          onClick={handleResetFilters}
          sx={{ textTransform: "capitalize" }}
        >
          Reset
        </Button>
      </Box>

      <Box
        sx={{
          borderRadius: "10px",
          overflowX: "auto",
          width: "100%",
          maxWidth: "90vw",
          margin: "auto",
        }}
      >
        <Box sx={{ minWidth: "1000px", width: "100%" }}>
          {/* Table Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "40px 30px 1.5fr 1fr 1fr 1fr 1fr 1fr 40px ",
                md: "60px 50px 2fr 1fr 1fr 1fr 1fr 1fr 60px ",
              },
              alignItems: "center",
              backgroundColor: "#2C5F8E",
              color: "#FFFFFF",
              fontSize: { xs: 12, md: 14 },
              fontWeight: 500,
              whiteSpace: "nowrap",
              fontFamily: "Geist, sans-serif",
              padding: "16px",
            }}
          >
            <Box />
            <Box>#</Box>
            <Box>Name</Box>
            <Box>Mobile</Box>
            <Box>Email </Box>
            <Box>Designation </Box>
            <Box>Profession </Box>
            <Box>Applied Jobs </Box>
            <Box sx={{ textAlign: "center" }}>Action </Box>
          </Box>

          {/* Table Rows */}
          {loaderOpen ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton
                key={idx}
                variant="rectangular"
                width="100%"
                height={50}
                sx={{ margin: "15px auto" }}
              />
            ))
          ) : CandidateData?.length > 0 ? (
            CandidateData.map((c, index) => (
              <Accordion
                key={c.CI_Id}
                expanded={expandedIds === c?.CI_Id}
                sx={{
                  boxShadow: "none",
                  borderBottom: "1px solid lightgrey",
                  margin: "0px auto",
                  "&:before": { display: "none" },
                  backgroundColor: index % 2 !== 0 ? `#EBF2FF` : "",
                }}
              >
                <AccordionSummary sx={{ padding: 0 }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "40px 30px 1.5fr 1fr 1fr 1fr 1fr 1fr 40px ",
                        md: "60px 50px 2fr 1fr 1fr 1fr 1fr 1fr 60px ",
                      },
                      alignItems: "center",
                      width: "100%",
                      gap: 1,
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {c?.applied_jobs?.length > 0 && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAccordionChange(c.CI_Id);
                          }}
                        >
                          <ExpandMore
                            sx={{
                              fontSize: "32px",
                              border: "2px solid #2C5F8E",
                              color: "#2C5F8E",
                              borderRadius: "50%",
                              transform:
                                expandedIds === c?.CI_Id
                                  ? "rotateX(180deg)"
                                  : "none",
                              transition: "0.4s ease-in-out",
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>

                    <Box>{index + 1 + page * rowsPerPage}</Box>

                    <Box display="flex" alignItems="center">
                      <Avatar
                        src={`${process.env.REACT_APP_BASE_URL}/${c?.CI_Photo}`}
                        sx={{ width: 36, height: 36, mr: 1 }}
                      >
                        {c.CI_Name ? c.CI_Name[0] : ""}
                      </Avatar>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.CI_Name} {c?.CI_Alias ? `(${c?.CI_Alias})` : ""}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.CI_PrimaryMobile ? c?.CI_PrimaryMobile : "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.CI_PrimaryEmail ? c?.CI_PrimaryEmail : "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.CI_Designation ? c?.CI_Designation : "-"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.Applied_profession_Name
                          ? c?.Applied_profession_Name
                          : "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">
                        {c?.applied_jobs?.length > 0
                          ? c.applied_jobs.length
                          : "-"}
                      </Typography>
                    </Box>

                    <Box>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => navigatetoView(c)}
                      >
                        <Visibility />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>

                {/* Accordion Details */}
                <AccordionDetails sx={{ px: 1, pb: 2, scale: "0.96" }}>
                  <Box
                    sx={{
                      minWidth: "800px",
                      overflowX: "auto",
                      border: "1px solid grey",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    {/* Table Header */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "60px 1fr 1fr 1fr 1fr",
                        },

                        backgroundColor: "#71aee5",
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: 14,
                        borderRadius: "8px 8px 0 0",
                        padding: "8px 10px",
                          fontFamily: "Geist, sans-serif",
                      }}
                    >
                      <Box>#</Box>
                      <Box>Job Title</Box>
                      <Box>Applied Date</Box>
                      <Box>Interview Status</Box>
                      <Box>Interview Date</Box>
                    </Box>

                    {/* Table Rows */}
                    {c?.applied_jobs?.length > 0 ? (
                      c.applied_jobs.map((j, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "60px 1fr 1fr 1fr 1fr",
                            },
                            alignItems: "center",
                            fontSize: 14,
                            py: 1,
                            borderBottom: "1px solid lightgrey",
                            backgroundColor: "white",
                            px: "10px",
                              fontFamily: "Geist, sans-serif",
                          }}
                        >
                          <Box>{i + 1}</Box>
                          <Box>{j?.job_applied || "-"}</Box>
                          <Box>{j?.JA_AppliedDate || "-"}</Box>
                          <Box>{j?.JobApplication_status || "-"}</Box>
                          <Box>{j?.JA_InterviewDate || "-"}</Box>
                        </Box>
                      ))
                    ) : (
                      <Box sx={{ textAlign: "center", py: 2 }}>
                        <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="body2">No jobs linked</Typography>
                      </Box>
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography sx={{  fontFamily: "Geist, sans-serif",}} variant="h6">No data available</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={CandidateData?.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default ClientCandidateList;
